import { memo } from 'react';
import {
  flexRender,
  PaginationState,
  RowSelectionState,
  ColumnPinningState,
  Table,
} from '@tanstack/react-table';
import { TableBody, Skeleton, TableRow, TableCell, cn } from '@sib/ui';
import { useIsLoadingDelayed } from '@sib/ui/hooks';
import {
  getTableColumnBorderVariable,
  getTableColumnPinningProperties,
  getTableColumnWidth,
  getRowStyling,
} from '../../utils';
import {
  DataTableDelayedLoadingIndicator,
  DataTableDelayedLoadingIndicatorProps,
} from './DataTableDelayedLoadingIndicator';
import {
  borderLeftPinnedColumnBg,
  borderRightPinnedColumnBg,
  pinnedColumnBg,
  pinnedLeftShadow,
  pinnedRightShadow,
} from './DataTableBody.style';

interface DataTableBodyProps<T> extends Pick<PaginationState, 'pageSize'> {
  table: Table<T>;
  rowSelection: RowSelectionState;
  columnPinning: ColumnPinningState;
  loading?: boolean;
  withDelayedLoading?: boolean;
  isEmpty?: boolean;
  emptyPlaceholder?: React.ReactNode;
  TableDelayedLoadingIndicatorProps: DataTableDelayedLoadingIndicatorProps;
  onRowClick?: (row: T) => void;
}

export const DataTableBody = <T,>({
  table,
  withDelayedLoading,
  emptyPlaceholder,
  isEmpty,
  pageSize,
  loading = false,
  TableDelayedLoadingIndicatorProps,
  onRowClick,
}: DataTableBodyProps<T>) => {
  const isLoadingDelayed = useIsLoadingDelayed({
    loading,
  });

  const columnsCount = table.getVisibleLeafColumns().length;

  return (
    <TableBody className="relative">
      {loading ? (
        <>
          {withDelayedLoading && (
            <DataTableDelayedLoadingIndicator
              {...TableDelayedLoadingIndicatorProps}
              visible={isLoadingDelayed}
              isEmpty={isEmpty}
            />
          )}

          {Array.from({
            length: pageSize,
          }).map((_, index) => (
            <TableRow key={`table-row-skeleton-${index}`}>
              <TableCell colSpan={columnsCount}>
                <Skeleton className="bg-surface-soft size-full rounded-sm" />
              </TableCell>
            </TableRow>
          ))}
        </>
      ) : !isEmpty ? (
        table.getRowModel().rows.map((row, index) => {
          const canRowExpand = row.getCanExpand();
          const handleRowClick = onRowClick
            ? () => onRowClick(row.original)
            : canRowExpand && (() => row.toggleExpanded());

          return (
            <TableRow
              key={row.id}
              {...(handleRowClick && {
                onClick: handleRowClick,
              })}
              className={cn(
                (canRowExpand || onRowClick) && 'cursor-pointer',
                getRowStyling({
                  row,
                  index,
                  rows: table.getRowModel().rows,
                })
              )}
            >
              {row.getVisibleCells().map(cell => {
                const cellContext = cell.getContext();
                const className = cellContext.column.columnDef.meta?.className;

                const aplicableClassName =
                  typeof className === 'string'
                    ? className
                    : className?.(row.original);

                const {
                  pinPosition,
                  isLeftPinned,
                  isRightPinned,
                  shouldApplyShadow,
                  style,
                } = getTableColumnPinningProperties(cell.column);

                return (
                  <TableCell
                    key={cell.id}
                    className={cn(
                      aplicableClassName,
                      // TODO: restore once have a way to handle resize indicator
                      // 'border-r-1',
                      '[&>button]:w-full',
                      'transition-colors duration-100',
                      pinPosition && 'sticky z-10',
                      pinPosition && !shouldApplyShadow && pinnedColumnBg,
                      isLeftPinned &&
                        shouldApplyShadow &&
                        `${pinnedLeftShadow} ${borderLeftPinnedColumnBg}`,
                      isRightPinned &&
                        shouldApplyShadow &&
                        `${pinnedRightShadow} ${borderRightPinnedColumnBg}`
                    )}
                    style={{
                      ...style,
                      width: getTableColumnWidth(cell.column.id),
                      borderRight: `var(${getTableColumnBorderVariable(cell.column.id)})`,
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cellContext)}
                  </TableCell>
                );
              })}
            </TableRow>
          );
        })
      ) : (
        emptyPlaceholder || null
      )}
    </TableBody>
  );
};

export const MemoizedDataTableBody = memo(DataTableBody, (prev, next) => {
  const areStatesEqual =
    JSON.stringify(prev.rowSelection) === JSON.stringify(next.rowSelection) &&
    prev.columnPinning.left?.join() === next.columnPinning.left?.join() &&
    prev.columnPinning.right?.join() === next.columnPinning.right?.join() &&
    prev.emptyPlaceholder === next.emptyPlaceholder;

  return (
    prev.table.options.data === next.table.options.data &&
    prev.isEmpty === next.isEmpty &&
    prev.loading === next.loading &&
    areStatesEqual
  );
}) as typeof DataTableBody;
